import React, { FormEvent, useState } from 'react'
import {
  Box,
  Link,
  Button,
  Typography,
  useTheme,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import { useAuth } from '../redux/slices/auth/useAuth'
import cca2List from '../lib/Countries/cca2.json';
import countries from '../lib/Countries/countries.json';
import moment from 'moment';
import { Months } from '../lib/Months';
import { SingupData, GenderType, LyfshortUserProfile } from '../types/authState'
import { userSchema } from '../utils/userSchemas';
import * as yup from 'yup'
import { useUser } from '../redux/slices/user/useUser';

type Props = {
  onCancel: () => Promise<void>
  onUpdateUser: (user: LyfshortUserProfile) => void
}

interface FormErrors {
  [key: string]: string;
}

const items = cca2List
  .map((cca2) => ({ ...(countries as Record<string, any>)[cca2], cca2 }))
  .map((c) => ({
    label: c.name.common,
    value: c.cca2,
  }));

const ProfileGetMinimumDetails = (props: Props) => {
  const { onCancel, onUpdateUser } = props
  const theme = useTheme()
  const { tempData } = useAuth()
  const { lyfshortUser } = useUser()

  const signupData: SingupData | null | undefined = tempData?.signupData;
  const user = tempData?.user
  const prefill: SingupData = { ...user, ...signupData } as SingupData

  let birthDate: string = moment().date(1).toString();
  let birthDateMonth: string = '';
  let birthDateYear: string = '';

  if (prefill.birthDate) {
    const parsedDate = new Date(prefill.birthDate)
    if (!isNaN(parsedDate.getTime())) {
      birthDate = parsedDate.toString();
      birthDateMonth = Months[parsedDate.getMonth()];
      birthDateYear = parsedDate.getFullYear().toString();
    }
  }

  const [fName, setFName] = useState(prefill.firstName)
  const [lName, setLName] = useState(prefill.lastName)
  const [username, setUsername] = useState(prefill.username)
  const [gender, setGender] = useState<GenderType>(prefill.sex)
  const [homeLocationCountry, setHomeLocationCountry] = useState<string>(prefill.homeLocationCountry ?? '')
  const [ageMonth, setAgeMonth] = useState<string>(birthDateMonth)
  const [ageYear, setAgeYear] = useState<string>(birthDateYear)
  const [progress, setProgress] = useState<boolean>(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [changedUsername, setChangedUsername] = useState<boolean>(false)
  const [hasWarnedAboutUsername, setHasWarnedAboutUsername] = useState<boolean>(!signupData?.isNewUser)

  let isNewUser = true
  let isNameRequired = true

  if (tempData && tempData.signupData) {
    isNewUser = !!tempData.signupData.isNewUser
    isNameRequired = !tempData.signupData.appleAccessToken
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (progress) return

    setErrors({})

    try {
      let birthDate = moment().date(1)

      if (ageMonth) birthDate.month(ageMonth)
      if (ageYear) birthDate.year(parseInt(ageYear))

      const signupData = tempData?.signupData
      const isAppleSignin = !!signupData?.appleAccessToken

      const data = {
        firstName: fName,
        lastName: lName,
        username: username,
        email: prefill.email,
        homeLocationCountry: homeLocationCountry,
        mobilePhone: prefill.mobilePhone,
        sex: gender,
        birthDate: birthDate.startOf('day').format('YYYY-MM-DD')
      }

      await userSchema.validate(data, {
        abortEarly: false,
        context: { isNameOptional: isAppleSignin, allocatedUsername: user?.username }
      })

      let allocatedUsername;

      // Prompt user once if username has not been changed/customised.
      if (tempData?.user) {
        allocatedUsername = tempData?.user.username
      }

      if (!hasWarnedAboutUsername &&
        (!changedUsername ||
          (allocatedUsername && username === allocatedUsername))
      ) {
        setHasWarnedAboutUsername(true)
        // TODO: Popup confirm modal
      }

      setProgress(true)

      onUpdateUser({ ...lyfshortUser, ...data })
    } catch (error) {
      const errors: FormErrors = {};
      if (error instanceof yup.ValidationError) {
        error.inner.forEach((err) => {
          const path = err.path || 'global';
          if (!errors[path]) {
            errors[path] =
              path === 'birthDate'
                ? 'You must be 13 years or older'
                : err.message.charAt(0).toUpperCase() + err.message.slice(1)
          }
        });
      } else {
        errors.global = (error as Error).message
      }
      setProgress(false);
      setErrors(errors)
    }
  }

  return (
    <div>
      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{
          color: theme.palette.grey[800],
          fontWeight: 500,
          mb: 3
        }}
      >
        Account Setup
      </Typography>
      {isNewUser && (
        <Typography textAlign="center">Please fill in the required fields.</Typography>
      )}
      {!isNewUser && (
        <Typography textAlign="center">
          Your profile is missing some required details, please fill in the required fields *
        </Typography>
      )}
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="First Name"
          variant="outlined"
          margin="normal"
          value={fName}
          onChange={(e) => setFName(e.target.value)}
          required
          type="text"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="Last Name"
          variant="outlined"
          margin="normal"
          value={lName}
          onChange={(e) => setLName(e.target.value)}
          required
          type="text"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="Username"
          variant="outlined"
          margin="normal"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          type="text"
          error={!!errors.username}
          helperText={errors.username}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <FormControl fullWidth sx={{ marginTop: 2 }}>
          <InputLabel id="country">Country</InputLabel>
          <Select
            labelId='country'
            id="country"
            label="Country"
            value={homeLocationCountry}
            onChange={(e) => setHomeLocationCountry(e.target.value)}
          >
            <MenuItem disabled selected value="">Country *</MenuItem>
            {items.map(item => (
              <MenuItem value={item.value} key={item.value}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box display="flex" alignItems="center" mt={2} gap={1}>
          <FormControl fullWidth>
            <InputLabel id="monthOfBirth">Month of Birth</InputLabel>
            <Select
              labelId='month-of-birth'
              id="month-of-birth"
              label="Month of Birth"
              value={ageMonth}
              required
              onChange={(e) => setAgeMonth(e.target.value)}
            >
              <MenuItem disabled selected value="">Month of Birth*</MenuItem>
              {Months.map((month) => (
                <MenuItem value={month.toString()} key={month}>{month.toString()}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Year of Birth"
            variant="outlined"
            margin="normal"
            value={ageYear}
            onChange={(e) => setAgeYear(e.target.value)}
            required
            type="number"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              margin: 0,
            }}
          />
        </Box>
        {errors.birthDate && <Typography color="#d32f2f" fontSize={12} ml={2}>{errors.birthDate}</Typography>}
        <FormControl fullWidth sx={{ marginTop: 2 }}>
          <InputLabel id="gender">Gender</InputLabel>
          <Select
            labelId='gender'
            id="gender"
            label="Gender"
            value={gender}
            required
            onChange={(e) => setGender(e.target.value as GenderType)}
          >
            <MenuItem disabled selected value="">Gender</MenuItem>
            <MenuItem value='f'>Female</MenuItem>
            <MenuItem value='m'>Male</MenuItem>
            <MenuItem value='o'>Non-binary</MenuItem>
          </Select>
        </FormControl>

        {!progress && <>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            sx={{
              mt: 3,
              mb: 2,
              height: 48,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Continue
          </Button>
          <Box textAlign="center">
            <Link
              component="button"
              sx={{
                cursor: 'pointer',
              }}
              onClick={onCancel}
            >
              Cancel
            </Link>
          </Box>
        </>}
        {progress &&
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        }
      </form>
    </div>
  )
}

export default ProfileGetMinimumDetails